import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, define, DEFINITION_KINDS } from '../types.js';

/**
 * Fuel reimbursement and authorization configuration.
 *
 * @remarks
 * **Field Naming Inconsistency Across Repos:**
 *
 * This schema supports both `min_fuel_price`/`max_fuel_price` (preferred) and
 * `min_fuel_spend`/`max_fuel_spend` (legacy) field names due to historical
 * inconsistencies across the codebase.
 *
 * **Current Usage:**
 * - `min_fuel_price` / `max_fuel_price`: Used in `sdk`, `admin-portal`, `admin-portal-v5`
 * - `min_fuel_spend` / `max_fuel_spend`: Used in `fuel-sdk`, `event-handlers`, `hasura-migrations`
 *
 * **Production Data:**
 * - Most production configs use `min_fuel_price` / `max_fuel_price`
 * - At least one customer (ID 3) still uses `min_fuel_spend` in production
 *
 * **Recommendation:**
 * - Use `min_fuel_price` / `max_fuel_price` for all new code
 * - Both field sets are currently supported for backwards compatibility
 * - When reading configs, check both field names if needed
 * - When writing configs, prefer `_price` fields
 *
 * **Migration Path:**
 * - Existing code using `_spend` fields should migrate to `_price` fields
 * - Database migrations should normalize `_spend` → `_price` over time
 * - Once all repos and production data are migrated, `_spend` fields can be removed
 */
export const schema = z
  .object({
    fuel_level: z
      .number()
      .min(0)
      .max(1)
      .describe('The expected fuel level at vehicle delivery (in percent).')
      .optional(),
    min_fuel_price: z
      .number()
      .nonnegative()
      .describe('Minimum fuel spend in dollars. Amounts below this will NOT be authorized. Prevents trivial purchases.')
      .optional(),
    max_fuel_price: z
      .number()
      .nonnegative()
      .describe(
        'Maximum fuel spend in dollars. Amounts above this will NOT be authorized at pump. Prevents excessive purchases.'
      )
      .optional(),
    min_fuel_spend: z
      .number()
      .nonnegative()
      .describe(
        'LEGACY: Alternative minimum fuel spend threshold. ' +
          'DEPRECATED: Use min_fuel_price instead. ' +
          'Still used in fuel-sdk, event-handlers, hasura-migrations. ' +
          'At least one production customer (ID 3) still uses this field. ' +
          'Both min_fuel_spend and min_fuel_price are supported for backwards compatibility.'
      )
      .optional(),
    max_fuel_spend: z
      .number()
      .nonnegative()
      .describe(
        'LEGACY: Alternative maximum fuel spend cap. ' +
          'DEPRECATED: Use max_fuel_price instead. ' +
          'Still used in fuel-sdk, event-handlers, hasura-migrations. ' +
          'Both max_fuel_spend and max_fuel_price are supported for backwards compatibility.'
      )
      .optional(),
  })
  .strict()
  .describe(
    'Fuel reimbursement and authorization configuration. Controls driver fuel spending limits and required fuel levels.'
  );

export type Fuel = z.infer<typeof schema>;

export const definition = define({
  key: 'fuel',
  schema,
  label: 'Fuel',
  description:
    'Fuel reimbursement and authorization configuration. Controls driver fuel spending limits and required fuel levels.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.FUEL.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    fuel_level: 0.0625,
    min_fuel_price: 5,
    max_fuel_price: 150,
  },
});
